/**
 * /ja/video-transcription — 動画/YouTube 文字起こし reframe bridge (JA).
 *
 * Targets 動画 文字起こし (4.9k) / youtube 文字起こし (5.6k) / 動画文字起こし無料.
 * No EN sibling. Links to the /ja/transcription pillar + meeting-minutes.
 */
import { MarketingShell } from './MarketingShell';

export function JaVideoTranscription() {
  return (
    <MarketingShell
      currentLocale="ja"
      hreflangPath="/video-transcription"
      hreflangLocales={['ja']}
      documentTitle="動画の文字起こし｜YouTube・会議動画を無料でテキスト化 · Draftlize"
      documentDescription="動画やYouTubeを無料で文字起こしする方法。会議動画は「文字」で終わらせず、決定を取り出して活かすところまで解説します。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">ガイド · 動画文字起こし</span>
          <h1 className="serif" style={{ fontSize: 'clamp(34px, 4.6vw, 56px)', lineHeight: 1.06, margin: '12px 0 24px' }}>
            動画を文字起こしして、<br />
            <em>要点だけを残す。</em>
          </h1>
          <p className="lede">
            会議の録画や YouTube を貼れば、数分で文字起こしが手に入ります。便利です。でも動画から起こした文字も、音声と同じで<em>素材</em>にすぎません。特に会議動画では、本当に必要なのは全発言ではなく、その中で下した<em>決定</em>とその理由。ここでは動画を無料で文字起こしする方法と、その先——文字ではなく決定を残すやり方——を解説します。
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">動画を無料で文字起こしする。</h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <ol style={{ fontSize: 16, lineHeight: 2, color: 'var(--ink-1)', margin: 0, paddingLeft: 22 }}>
            <li><strong>動画を用意</strong>——会議の録画、画面収録、または YouTube の URL。</li>
            <li><strong>文字起こし</strong>——動画対応の AI ツールが音声トラックをテキスト化。多くが無料枠あり。</li>
            <li><strong>整える</strong>——話者や専門用語を直して書き出し。</li>
          </ol>
          <p style={{ fontSize: 16, lineHeight: 1.8, color: 'var(--ink-1)', margin: '20px 0 0' }}>
            YouTube 動画も同じ手順です。字幕をそのまま使う手もありますが、字幕は「読むための文字」、文字起こしは「使うための文字」。そして会議動画なら、その先にもう一歩あります。まず <a href="/ja/transcription" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>音声の文字起こし</a> の考え方も参照。
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">字幕 vs 決定</span>
            <h2 className="serif">動画の文字化で終わらせず、<em>決定を残す。</em></h2>
          </div>
          <p>文字起こしツールと Draftlize は別の仕事をします。片方は動画を文字にし、もう片方はその中の決定を生かし続ける。</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th>動画文字起こし</th>
              <th className="ours">Draftlize</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>出力</td><td className="partial">字幕・全文テキスト</td><td className="ours yes">決定カード（理由つき）</td></tr>
            <tr><td>会議動画の価値</td><td className="no">全発言に埋もれる</td><td className="ours yes">数個の決定に凝縮</td></tr>
            <tr><td>あとで見返す</td><td className="no">長すぎて誰も見ない</td><td className="ours yes">ID で参照・最新か一目</td></tr>
            <tr><td>AI エージェント</td><td className="partial">字幕を読むだけ</td><td className="ours yes">MCP で決定を読み書き</td></tr>
          </tbody>
        </table>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          1時間の会議動画を全部文字起こしできても、そのうちどの決定がまだ有効かは、文字は教えてくれない。
        </blockquote>
        <cite>動画の文字起こしはツールに。決定を生かすのは Draftlize。</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">よくある質問。</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { q: '動画の文字起こしにおすすめのツールは？', a: '動画対応の AI 文字起こしツールを使えば、音声トラックを自動でテキスト化できます。Draftlize は文字起こしはしません——起こした文字の中の決定をカード化して最新に保つ役割です。' },
            { q: 'YouTube を文字起こしするには？', a: 'YouTube の URL を対応ツールに渡すか、自動字幕を書き出します。会議やインタビュー動画なら、その先に「決定を残す」一歩を足すと価値が変わります。' },
            { q: '多言語の動画も文字起こしできる？', a: '多くのツールが多言語に対応します。ただ言語より大事なのは、起こした後にどの決定を残すか。Draftlize はそこを担います。' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.9, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">会議動画から、決定だけを生きたカードに<em>——$5 無料で。</em></h2>
        </div>
        <div className="trust-strip">
          <span>新規登録で $5 付与</span>
          <span>使った分だけ支払い</span>
          <span>残高は無期限</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            動画の文字起こしはツールに任せて、要約を Draftlize に貼るだけ。会議の決定が、リンクされ最新で AI も読めるカードになります。
          </p>
          <a className="btn-primary" href="/">$5 無料で始める</a>
        </div>
      </section>
    </MarketingShell>
  );
}
